import { useRef, useState } from 'react';

import { IconCheckDefault, IconTimesDefault, IconUpload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';

import { countCsvDataRows } from './countCsvDataRows';
import { ExportRequest } from './exportDateRange';
import { ExportSettingsDrawer } from './ExportSettingsDrawer';
import { LoadingModal } from '../../../../components/LoadingModal/LoadingModal';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { downloadAsAttachment } from '../../../../Pages/lendingApiFetch';
import { createToast } from '../../../../store/IwToast';
import { TabConfig } from '../../CustomersV2.types';
import { useCustomersTableState } from '../../hooks/useCustomersTableState';
import { useExportSettings } from '../../hooks/useExportSettings';
import { TableSettings } from '../../hooks/useTableSettings';
import { trackExportClicked } from '../../tracking';

export const ExportCustomers = ({
  config,
  settings,
}: {
  config: TabConfig;
  settings: TableSettings;
}) => {
  const { stateKey } = useStateKey();
  const { state } = useCustomersTableState(config, settings);
  const { settings: exportSettings, setAcrossBothTabs } = useExportSettings();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [requestId, setRequestId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const isRunningRef = useRef(false);

  // The same filter params the table sends, so the CSV covers exactly the rows
  // on screen. `ordering` and `include_latest_payment` are both absent: the
  // endpoint fixes the CSV's row order (earliest interacted date, descending)
  // and its columns server-side, so neither has anything to change here.
  //
  // Exporting across both tabs is the absence of a view: with no view the API
  // returns every partition. The pill filter comes off because it's
  // tab-specific (`application_stage` on one tab, `spending_limit_status` on
  // the other) and means nothing across the union. `include_declined` comes
  // off too, but because an absent view already returns declined customers —
  // sending it would be redundant, not because it's tab-specific.
  const buildQuery = ({ dateFrom, dateTo }: ExportRequest) =>
    new URLSearchParams({
      ...(exportSettings.acrossBothTabs
        ? {}
        : {
            view: config.view,
            ...(state.filterValue && {
              [config.filterParam]: state.filterValue,
            }),
            ...(settings.declined && { include_declined: 'true' }),
          }),
      ...(state.search && {
        search: state.search,
        search_fields: 'company_name',
      }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    });

  const handleDownload = async (request: ExportRequest) => {
    // Guarded on a ref rather than disabling the drawer's Export alone: Orion's
    // `Drawer` is a Headless UI `Transition`, so the button stays mounted
    // through the drawer's leave transition after `onExport` fires, and a
    // second click there before it unmounts must not dispatch again.
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    const { acrossBothTabs } = exportSettings;
    trackExportClicked({
      tab: config.path,
      filterName: acrossBothTabs ? null : state.filterValue,
      includeDeclined: acrossBothTabs || settings.declined,
      dateRange: request.preset,
      acrossBothTabs,
    });
    setIsLoading(true);
    setRequestId((previous) => previous + 1);

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      // eslint-disable-next-line iwoca/prefer-lapi-client
      const response = await fetch(
        `/api/iwocapay/seller/${stateKey}/customers_download/?${buildQuery(request)}`,
        { signal: controller.signal },
      );
      // The status, not the download helper, decides success: that helper only
      // throws when Content-Disposition is missing, so an error response
      // carrying the header would be saved as the seller's CSV.
      if (!response.ok) {
        throw new Error(`Customers export failed: ${response.status}`);
      }
      const customerCount = countCsvDataRows(await response.clone().text());
      await downloadAsAttachment(response);
      createToast(
        <>
          <b>
            Success - {customerCount} customer{customerCount === 1 ? '' : 's'}
          </b>{' '}
          exported as CSV.
        </>,
        {
          className:
            'flex items-start gap-s border-success-mid !bg-success-lt text-s text-primary-10',
          icon: (
            <IconCheckDefault width={20} height={20} className="shrink-0" />
          ),
        },
      );
    } catch {
      // Aborting is the seller dismissing the modal, not a failure to report.
      if (controller.signal.aborted) return;
      createToast(
        <>
          <b>Failed -</b> Sorry your export failed. Please try again.
        </>,
        {
          variant: 'failure',
          className:
            'flex items-start gap-s border-core-90 text-s text-primary-10',
          icon: (
            <IconTimesDefault width={20} height={20} className="shrink-0" />
          ),
        },
      );
    } finally {
      isRunningRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-xs">
      {/* Disabled for the whole request, not just while the modal is up: the
          modal's show delay would otherwise leave a window where a second
          export starts and orphans the first abort controller. */}
      <Button
        variant="secondary-alt"
        size="small"
        className="flex items-center gap-s"
        disabled={isLoading}
        onClick={() => setIsDrawerOpen(true)}
      >
        Export
        <IconUpload height={14} width={14} />
      </Button>
      <ExportSettingsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        acrossBothTabs={exportSettings.acrossBothTabs}
        onAcrossBothTabsChange={setAcrossBothTabs}
        onExport={(request) => {
          setIsDrawerOpen(false);
          void handleDownload(request);
        }}
      />
      {/* Keyed on the request so dismissing one export doesn't leave the next
          one without a modal. */}
      <LoadingModal
        key={requestId}
        isOpen={isLoading}
        onClose={() => {
          controllerRef.current?.abort();
          setIsLoading(false);
        }}
        dismissable
        title="We’re getting your export ready"
        subText="Since you’re exporting a large file this might take longer than usual."
        tip="Speed things up next time by applying more filters. The more filters you have on, the smaller the file is, so it’s faster to export."
      />
    </div>
  );
};
